import type { Filters, VehicleType, ParkingType, DurationType } from '../types';
import { VEHICLE_LABELS, PARKING_LABELS, DURATION_LABELS, SECURITY_LABELS } from '../types';
import { trackEvent } from '../services/analytics';
import type { FilterKey } from '../services/analytics';
import HelpTip from './HelpTip';

interface FilterPanelProps {
  filters: Filters;
  onChange: (filters: Filters) => void;
  onClear: () => void;
}

export default function FilterPanel({ filters, onChange, onClear }: FilterPanelProps) {
  const updateFilter = <K extends keyof Filters>(key: K, value: Filters[K]) => {
    let valueCategory: string;
    if (key === 'security') {
      const arr = value as unknown as string[];
      const prev = filters.security;
      const added = arr.find((f) => !prev.includes(f));
      const removed = prev.find((f) => !arr.includes(f));
      valueCategory = added ? `added:${added}` : removed ? `removed:${removed}` : 'set';
    } else if (typeof value === 'boolean') {
      valueCategory = value ? 'on' : 'off';
    } else if (value === '' || value === null || value === undefined) {
      valueCategory = 'cleared';
    } else {
      valueCategory = String(value);
    }
    trackEvent('filter_changed', {
      filter_key: key as FilterKey,
      value_category: valueCategory,
    });
    onChange({ ...filters, [key]: value });
  };

  const toggleSecurity = (feature: string) => {
    const current = filters.security;
    const updated = current.includes(feature)
      ? current.filter((f) => f !== feature)
      : [...current, feature];
    updateFilter('security', updated);
  };

  const handleClear = () => {
    const activeCount = Object.entries(filters).filter(([key, value]) => {
      if (key === 'security') return (value as string[]).length > 0;
      if (typeof value === 'boolean') return value;
      return value !== '';
    }).length;
    trackEvent('filters_cleared', { active_count_before: activeCount });
    onClear();
  };

  const activeCount = Object.entries(filters).filter(([key, value]) => {
    if (key === 'security') return (value as string[]).length > 0;
    if (typeof value === 'boolean') return value;
    return value !== '';
  }).length;

  return (
    <div className="bg-gray-50 rounded-xl p-3 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <h3 className="font-semibold text-gray-900 text-sm">Filters</h3>
          <HelpTip tipKey="filters" label="What do filters do?" />
        </div>
        {activeCount > 0 && (
          <button
            onClick={handleClear}
            className="text-xs text-parksy-600 hover:text-parksy-700 font-medium"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Vehicle type */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Vehicle Type</label>
        <select
          value={filters.vehicle_type}
          onChange={(e) => updateFilter('vehicle_type', e.target.value as VehicleType | '')}
          className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-parksy-500 focus:border-transparent"
        >
          <option value="">Any</option>
          {(Object.keys(VEHICLE_LABELS) as VehicleType[]).map((v) => (
            <option key={v} value={v}>
              {VEHICLE_LABELS[v]}
            </option>
          ))}
        </select>
      </div>

      {/* Parking type */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Parking Type</label>
        <select
          value={filters.parking_type}
          onChange={(e) => updateFilter('parking_type', e.target.value as ParkingType | '')}
          className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-parksy-500 focus:border-transparent"
        >
          <option value="">Any</option>
          {(Object.keys(PARKING_LABELS) as ParkingType[]).map((p) => (
            <option key={p} value={p}>
              {PARKING_LABELS[p]}
            </option>
          ))}
        </select>
      </div>

      {/* Duration */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Duration</label>
        <select
          value={filters.duration}
          onChange={(e) => updateFilter('duration', e.target.value as DurationType | '')}
          className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-parksy-500 focus:border-transparent"
        >
          <option value="">Any</option>
          {(Object.keys(DURATION_LABELS) as DurationType[]).map((d) => (
            <option key={d} value={d}>
              {DURATION_LABELS[d]}
            </option>
          ))}
        </select>
      </div>

      {/* Free / Paid */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Pricing</label>
        <select
          value={filters.free}
          onChange={(e) => updateFilter('free', e.target.value as Filters['free'])}
          className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-parksy-500 focus:border-transparent"
        >
          <option value="any">Any</option>
          <option value="free">Free only</option>
          <option value="paid">Paid only</option>
        </select>
      </div>

      {/* Price range */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Price Range (₹)</label>
        <div className="flex gap-2">
          <input
            type="number"
            placeholder="Min"
            value={filters.min_price}
            onChange={(e) => updateFilter('min_price', e.target.value)}
            className="w-1/2 text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-parksy-500 focus:border-transparent"
          />
          <input
            type="number"
            placeholder="Max"
            value={filters.max_price}
            onChange={(e) => updateFilter('max_price', e.target.value)}
            className="w-1/2 text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-parksy-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Security */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Security</label>
        <div className="flex flex-wrap gap-2">
          {Object.entries(SECURITY_LABELS).map(([key, label]) => (
            <button
              key={key}
              onClick={() => toggleSecurity(key)}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${
                filters.security.includes(key)
                  ? 'bg-parksy-100 border-parksy-500 text-parksy-700'
                  : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Toggles */}
      <div className="space-y-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={filters.covered}
            onChange={(e) => updateFilter('covered', e.target.checked)}
            className="rounded border-gray-300 text-parksy-600 focus:ring-parksy-500"
          />
          <span className="text-sm text-gray-700">Covered parking</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={filters.access_24x7}
            onChange={(e) => updateFilter('access_24x7', e.target.checked)}
            className="rounded border-gray-300 text-parksy-600 focus:ring-parksy-500"
          />
          <span className="text-sm text-gray-700">24/7 access</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={filters.ev}
            onChange={(e) => updateFilter('ev', e.target.checked)}
            className="rounded border-gray-300 text-parksy-600 focus:ring-parksy-500"
          />
          <span className="text-sm text-gray-700">EV charging</span>
        </label>
      </div>
    </div>
  );
}
